import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react"
import { WingsButton, WingsEyebrow } from "@wingspans-ui"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faChevronDown, faChevronUp, faTimes } from "@fortawesome/free-solid-svg-icons"
import { FormattedMessage, useIntl } from "react-intl"

import "./ExploreUnifiedFiltersDropdown.css"

const POPOVER_WIDTH = 32 * 16
const POPOVER_MARGIN = 0.5 * 16
const POPOVER_VERTICAL_OFFSET = 0.5 * 16
const POPOVER_MIN_OPEN_HEIGHT = 14 * 16
const POPOVER_MAX_HEIGHT = 30 * 16
const TRIGGER_SCROLL_TOP_GAP = 8
const POPOVER_Z_INDEX = 11

/**
 * Shared Explore filters popover with an accordion-based UI.
 * This component is reusable for any Explore page that needs sectioned checkbox filters.
 *
 * @param {Object} props
 * @param {Array<{id: string, title: string, options: Array<{id: string, label: string, count?: number}>, columns?: number}>} props.sections
 * @param {string[]} props.selectedOptionIds
 * @param {(optionId: string) => void} props.onToggleOption
 * @param {() => void} props.onClearAll
 * @param {string} [props.viewLabel]
 * @param {() => void} [props.onView]
 * @param {string} props.heading
 * @param {string} [props.popoverClassName]
 * @returns {JSX.Element}
 */
const ExploreUnifiedFiltersDropdown = ({
  sections,
  selectedOptionIds,
  onToggleOption,
  onClearAll,
  viewLabel = "",
  onView,
  heading,
  popoverClassName = "",
}) => {
  const { formatMessage } = useIntl()

  const [isOpen, setIsOpen] = useState(false)
  const [openSectionId, setOpenSectionId] = useState(null)
  const [popoverStyle, setPopoverStyle] = useState({})

  const triggerRef = useRef(null)
  const popoverRef = useRef(null)
  const sectionScrollRefs = useRef({})

  const selectedSet = useMemo(() => new Set(selectedOptionIds), [selectedOptionIds])

  const optionMap = useMemo(() => {
    const map = new Map()

    sections.forEach((section) => {
      section.options.forEach((option) => {
        map.set(option.id, option)
      })
    })

    return map
  }, [sections])

  const selectedOptions = useMemo(() => {
    return selectedOptionIds.map((optionId) => optionMap.get(optionId)).filter(Boolean)
  }, [selectedOptionIds, optionMap])

  const computePosition = useCallback(() => {
    if (!triggerRef.current) {
      return
    }

    const triggerRect = triggerRef.current.getBoundingClientRect()
    const viewportWidth = window.innerWidth
    const viewportHeight = window.innerHeight

    const popoverWidth = Math.min(POPOVER_WIDTH, viewportWidth - POPOVER_MARGIN * 2)

    // Keep a consistent medium-screen style: right-edge aligned to the trigger,
    // so the panel opens below and shifted to the left of the button.
    let left = triggerRect.right - popoverWidth
    left = Math.max(POPOVER_MARGIN, left)
    left = Math.min(left, viewportWidth - popoverWidth - POPOVER_MARGIN)

    const spaceBelow = viewportHeight - triggerRect.bottom - POPOVER_MARGIN
    const top = triggerRect.bottom + POPOVER_VERTICAL_OFFSET
    const maxHeight = Math.max(POPOVER_MIN_OPEN_HEIGHT, Math.min(POPOVER_MAX_HEIGHT, spaceBelow))

    setPopoverStyle({
      position: "fixed",
      left,
      top,
      width: popoverWidth,
      maxHeight,
      zIndex: POPOVER_Z_INDEX,
    })
  }, [])

  useLayoutEffect(() => {
    if (!isOpen) {
      return
    }

    computePosition()
  }, [isOpen, computePosition, sections])

  useEffect(() => {
    if (!isOpen) {
      return
    }

    const handleScroll = () => computePosition()
    const handleResize = () => computePosition()

    window.addEventListener("scroll", handleScroll, true)
    window.addEventListener("resize", handleResize)

    return () => {
      window.removeEventListener("scroll", handleScroll, true)
      window.removeEventListener("resize", handleResize)
    }
  }, [isOpen, computePosition])

  useEffect(() => {
    if (!isOpen) {
      return
    }

    const handleOutsideClick = (event) => {
      if (
        popoverRef.current
        && !popoverRef.current.contains(event.target)
        && triggerRef.current
        && !triggerRef.current.contains(event.target)
      ) {
        setIsOpen(false)
      }
    }

    document.addEventListener("mousedown", handleOutsideClick)

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick)
    }
  }, [isOpen])

  const toggleSection = (sectionId) => {
    setOpenSectionId((previousValue) => {
      const nextSectionId = previousValue === sectionId ? null : sectionId

      if (nextSectionId && sectionScrollRefs.current[nextSectionId]) {
        sectionScrollRefs.current[nextSectionId].scrollTop = 0
      }

      return nextSectionId
    })
  }

  const scrollTriggerToViewportTop = useCallback(() => {
    if (typeof window === "undefined" || !triggerRef.current) {
      return
    }

    const bodyPaddingTop = Number.parseFloat(window.getComputedStyle(document.body).paddingTop) || 0
    const triggerTop = triggerRef.current.getBoundingClientRect().top
    const targetTop = Math.max(0, window.scrollY + triggerTop - bodyPaddingTop - TRIGGER_SCROLL_TOP_GAP)

    window.scrollTo({
      top: targetTop,
      behavior: "smooth",
    })
  }, [])

  const handleTriggerClick = () => {
    setIsOpen((previousValue) => {
      const nextValue = !previousValue

      if (nextValue) {
        if (typeof window !== "undefined" && window.innerWidth <= 760) {
          scrollTriggerToViewportTop()
        }
        computePosition()
      }

      return nextValue
    })
  }

  const handleBackdropMouseDown = (event) => {
    event.preventDefault()
    event.stopPropagation()
    setIsOpen(false)
  }

  const handleViewClick = () => {
    if (typeof onView === "function") {
      onView()
    }

    setIsOpen(false)
  }

  const closedPopoverStyle = {
    position: "fixed",
    left: -9999,
    top: -9999,
  }

  const activePopoverStyle = isOpen ? popoverStyle : closedPopoverStyle

  const isSmallViewport = typeof window !== "undefined" && window.innerWidth <= 760
  const computedColumns = isSmallViewport ? 2 : 3

  return (
    <>
      <button
        ref={triggerRef}
        className={`stream-explore-filters-button explore-unified-filters-button ${isOpen ? "is-open" : ""}`.trim()}
        onClick={handleTriggerClick}
      >
        <FormattedMessage id="more-filters" defaultMessage="Filters" />

        <FontAwesomeIcon
          className={`explore-unified-filters-button-icon ${isOpen ? "hide" : "show"}`}
          icon={faChevronDown}
        />
        <FontAwesomeIcon
          className={`explore-unified-filters-button-icon ${isOpen ? "show" : "hide"}`}
          icon={faChevronUp}
        />
      </button>

      {isOpen && (
        <div
          className="explore-unified-filters-backdrop"
          onMouseDown={handleBackdropMouseDown}
        />
      )}

      <div
        ref={popoverRef}
        style={activePopoverStyle}
        className={`explore-unified-filters-popover ${popoverClassName} ${isOpen ? "show" : "hide"}`.trim()}
      >
        <div className="explore-unified-filters-popover-shell">
          <div className="explore-unified-filters-header">
            <WingsEyebrow as="p" className="explore-unified-filters-eyebrow" color="blue">
              {heading}
            </WingsEyebrow>
          </div>

          {selectedOptions.length > 0 && (
            <div className="explore-unified-filters-chips">
              <div className="explore-unified-filters-chip-list">
                {selectedOptions.map((option) => {
                  return (
                    <button
                      key={option.id}
                      className="explore-unified-filters-chip"
                      onClick={() => onToggleOption(option.id)}
                    >
                      <span>{option.label}</span>
                      <FontAwesomeIcon icon={faTimes} />
                    </button>
                  )
                })}
              </div>
            </div>
          )}

          <div className="explore-unified-filters-sections">
            {sections.map((section) => {
              const isSectionOpen = openSectionId === section.id
              const sectionColumns = Math.min(section.columns || computedColumns, computedColumns)

              return (
                <div key={section.id} className="explore-unified-filters-section">
                  <button
                    className={`explore-unified-filters-section-toggle ${isSectionOpen ? "is-selected" : ""}`.trim()}
                    onClick={() => toggleSection(section.id)}
                  >
                    <span className="explore-unified-filters-section-title-wrap">
                      <span className="w-h6">{section.title}</span>
                    </span>

                    <FontAwesomeIcon
                      className={`explore-unified-filters-section-icon ${isSectionOpen ? "open" : "closed"}`}
                      icon={faChevronDown}
                    />
                  </button>

                  <div
                    ref={(element) => {
                      sectionScrollRefs.current[section.id] = element
                    }}
                    className={`explore-unified-filters-options-wrap ${isSectionOpen ? "open" : "closed"}`}
                  >
                    <div
                      className="explore-unified-filters-options-grid"
                      style={{ gridTemplateColumns: `repeat(${sectionColumns}, minmax(0, 1fr))` }}
                    >
                      {section.options.map((option) => {
                        const optionInputId = `explore-filter-${section.id}-${option.id}`
                        const isChecked = selectedSet.has(option.id)

                        return (
                          <label
                            key={option.id}
                            htmlFor={optionInputId}
                            className="explore-unified-filters-option"
                          >
                            <input
                              id={optionInputId}
                              className="explore-unified-filters-option-input"
                              type="checkbox"
                              checked={isChecked}
                              onChange={() => onToggleOption(option.id)}
                            />

                            <span className="explore-unified-filters-option-label" title={option.label}>
                              {option.label}
                            </span>
                          </label>
                        )
                      })}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>

          {selectedOptions.length > 0 && (
            <div className="explore-unified-filters-footer">
              <button
                className="explore-unified-filters-clear-button"
                onClick={onClearAll}
              >
                {formatMessage({ id: "careers-explore-clear-filters", defaultMessage: "Clear Filters" })}
              </button>

              <WingsButton
                variant="primary"
                size="mini"
                className="explore-unified-filters-view-button"
                onClick={handleViewClick}
              >
                {formatMessage(
                  { id: "explore-filters-view-cta", defaultMessage: "View {label}" },
                  { label: viewLabel }
                )}
              </WingsButton>
            </div>
          )}
        </div>
      </div>
    </>
  )
}

export default ExploreUnifiedFiltersDropdown
